// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Box from "../../resources/layout-and-structure/Box.res.js";
import * as Auth from "../../bundles/Auth/Auth.res.js";
import * as Card from "../../resources/layout-and-structure/Card.res.js";
import * as I18n from "../../primitives/I18n.res.js";
import * as React from "react";
import * as Inline from "../../resources/layout-and-structure/Inline.res.js";
import * as Tooltip from "../../resources/overlays/Tooltip.res.js";
import * as TableView from "../../resources/tables/TableView.res.js";
import * as TextStyle from "../../resources/typography/TextStyle.res.js";
import * as Belt_Array from "@rescript/runtime/lib/es6/Belt_Array.js";
import * as TooltipIcon from "../../resources/overlays/TooltipIcon.res.js";
import * as Stdlib_Array from "@rescript/runtime/lib/es6/Stdlib_Array.js";
import * as CatalogProduct from "./CatalogProduct.res.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as JsxRuntime from "react/jsx-runtime";
import * as CatalogTableActions from "./CatalogTableActions.res.js";
import * as CatalogProductNameTableCell from "./CatalogProductNameTableCell.res.js";
import * as CatalogVariantStockThresholdCell from "./CatalogVariantStockThresholdCell.res.js";
import * as PricePurchaseTableEditCellWrapper from "../Price/PricePurchaseTableEditCellWrapper.res.js";

function keyExtractor(row) {
  return row.id + "-variant";
}

let Row = {
  keyExtractor: keyExtractor
};

let make = React.memo(props => {
  let retailPrices = props.retailPrices;
  let ref = React.useRef(null);
  return JsxRuntime.jsx("div", {
    children: JsxRuntime.jsxs(Inline.make, {
      children: [
        JsxRuntime.jsx(TextStyle.make, {
          children: props.formattedRetailPrice
        }),
        retailPrices.length !== 0 ? JsxRuntime.jsx(TooltipIcon.make, {
            children: retailPrices.map(param => {
              let formattedTaxType = I18n.t(param.taxIncluded ? "VAT incl." : "VAT excl.");
              let formattedPrice = param.name + ` ` + formattedTaxType + ` :  ` + param.formattedValue + `\n`;
              return JsxRuntime.jsxs(JsxRuntime.Fragment, {
                children: [
                  JsxRuntime.jsx(Tooltip.Span.make, {
                    text: formattedPrice,
                    bold: param.default
                  }),
                  JsxRuntime.jsx(Tooltip.Gap.make, {
                    space: "xxsmall"
                  })
                ]
              });
            }),
            variation: "info",
            altTriggerRef: ref
          }) : null
      ],
      space: "small"
    }),
    ref: Primitive_option.some(ref)
  });
});

let RetailPriceDetailsCell = {
  make: make
};

function tableColumns(allShopsFiltered) {
  return [
    {
      key: "variant-name",
      name: I18n.t("Name"),
      layout: {
        minWidth: {
          NAME: "px",
          VAL: 170
        },
        width: {
          NAME: "fr",
          VAL: 1.5
        }
      },
      render: param => {
        let match = param.data;
        let shopName = allShopsFiltered ? match.shopName : undefined;
        return JsxRuntime.jsx(CatalogProductNameTableCell.make, {
          cku: match.cku,
          name: match.name,
          shopName: shopName
        });
      }
    },
    {
      key: "variant-purchaseprice",
      name: I18n.t("Purchase price"),
      layout: {
        minWidth: {
          NAME: "px",
          VAL: 120
        }
      },
      render: param => {
        let match = param.data;
        let formattedPurchasePrice = match.formattedPurchasePrice;
        if (formattedPurchasePrice === "VARIABLE") {
          return JsxRuntime.jsx(TextStyle.make, {
            children: I18n.t("Differing"),
            variation: "normal",
            size: "small"
          });
        }
        return JsxRuntime.jsx(PricePurchaseTableEditCellWrapper.make, {
          value: match.purchasePrice,
          formattedPurchasePrice: formattedPurchasePrice,
          bulkUnit: match.bulkUnit,
          variantId: match.id
        });
      }
    },
    {
      key: "variant-retailprice",
      name: I18n.t("Retail price"),
      layout: {
        minWidth: {
          NAME: "px",
          VAL: 120
        }
      },
      render: param => {
        let match = param.data;
        return JsxRuntime.jsx(make, {
          formattedRetailPrice: match.formattedRetailPrice,
          retailPrices: match.retailPrices
        });
      }
    },
    {
      key: "variant-stock",
      name: I18n.t("Stock"),
      layout: {
        minWidth: {
          NAME: "px",
          VAL: 120
        }
      },
      render: param => {
        let match = param.data;
        let stock = match.stock;
        return JsxRuntime.jsx(CatalogVariantStockThresholdCell.make, {
          stockQuantity: stock.quantity,
          stockState: stock.state,
          formattedShopsNames: match.shopName,
          id: match.id,
          maxStockThreshold: match.maxStockThreshold,
          minStockThreshold: match.minStockThreshold,
          stockOrderTriggerThreshold: match.stockOrderTriggerThreshold,
          bulk: match.bulk
        });
      }
    },
    {
      key: "variant-status",
      name: I18n.t("Status"),
      render: param => {
        let status = param.data.status;
        if (status !== undefined) {
          return JsxRuntime.jsx(CatalogProduct.Status.Badge.make, {
            value: status
          });
        } else {
          return JsxRuntime.jsx(TextStyle.make, {
            children: I18n.t("Differing"),
            variation: "normal"
          });
        }
      }
    },
    {
      key: "variant-actions",
      layout: {
        width: {
          NAME: "fr",
          VAL: 0
        },
        alignX: "flexEnd"
      },
      render: param => {
        let match = param.data;
        return JsxRuntime.jsx(CatalogTableActions.make, {
          cku: match.cku,
          id: match.id,
          status: match.status
        });
      }
    }
  ];
}

function CatalogProductVariantTableCard(props) {
  let match = Auth.useScope();
  let allShopsFiltered;
  allShopsFiltered = match.TAG === "Organisation" ? match.activeShop === undefined : false;
  let columns = React.useMemo(() => tableColumns(allShopsFiltered), [allShopsFiltered]);
  let rows = Stdlib_Array.reduce(props.shopsProductVariants, [], (acc, arr) => Belt_Array.concatMany([
    acc,
    arr
  ]));
  return JsxRuntime.jsx(Card.make, {
    children: JsxRuntime.jsx(Box.make, {
      children: JsxRuntime.jsx(TableView.make, {
        data: {
          TAG: "Done",
          _0: {
            TAG: "Ok",
            _0: rows
          }
        },
        columns: columns,
        keyExtractor: keyExtractor,
        hideCard: true,
        maxHeight: 500,
        placeholderEmptyState: JsxRuntime.jsx(Box.make, {
          children: JsxRuntime.jsx(Inline.make, {
            children: JsxRuntime.jsx(TextStyle.make, {
              children: I18n.t("No product variant has been yet recorded"),
              align: "center",
              variation: "normal",
              size: "small"
            }),
            align: "center"
          }),
          spaceX: "large",
          spaceY: "xlarge"
        })
      }),
      spaceTop: "small"
    }),
    title: I18n.t("Product's variants"),
    variation: "table",
    grow: true
  });
}

let make$1 = React.memo(CatalogProductVariantTableCard);

let Config;

export {
  Config,
  Row,
  RetailPriceDetailsCell,
  tableColumns,
  make$1 as make,
}
/* make Not a pure module */
